import { useCallback, useMemo } from "react";
import { Flex } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { FormField, FormSchema } from "@/v2/components/form/form-schema";
import DynamicForm from "@/v2/components/form/Form";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";

const EMPTY_ALLOWED_FIELD_LABELS = new Set<string>([
  "Email",
  "Subscriptionplanname",
]);

interface AddContextFieldsDialogProps {
  fields: FormField[];
  values?: Record<string, any>;
  onClose: () => void;
  onSubmit: (values: Record<string, any>) => void;
}

export function AddContextFieldsDialog({
  values = {},
  onClose,
  onSubmit,
  ...props
}: AddContextFieldsDialogProps) {
  const hardcodeFields = useFlag("hardcodeFields");
  const { workspace } = useWorkspace();

  const formSchema = useMemo<FormSchema>(() => {
    let fields = [...props.fields];

    const yotpoFields: FormField[] = [
      {
        id: "guid",
        label: "guid",
        kind: "text",
      },
      {
        id: "api_key",
        label: "api_key",
        kind: "text",
      },
    ];

    const quackPlainFields: FormField[] = [
      {
        id: "simulatorId",
        label: "simulatorId",
        kind: "text",
      },
    ];

    if (hardcodeFields && workspace === "quack-plain") {
      quackPlainFields.forEach((field) => {
        if (!fields.some((f) => f.id === field.id)) {
          fields.push(field);
        }
      });
    }

    if (hardcodeFields && workspace?.includes("yotpo")) {
      yotpoFields.forEach((field) => {
        if (!fields.some((f) => f.id === field.id)) {
          fields.push(field);
        }
      });
    }

    Object.entries(values).forEach(([key, value]) => {
      if (!fields.some((field) => field.id === key)) {
        fields.push({
          id: key,
          label: key,
          kind: "text",
        });
      }
    });

    return {
      version: "1.0",
      id: "add-context-fields",
      sections: [
        {
          id: "add-context-fields",
          fields: fields.map((field) => {
            if (!EMPTY_ALLOWED_FIELD_LABELS.has(field.label)) return field;

            const base = { ...field, required: false };

            if (
              base.kind === "select" &&
              !base.multiple &&
              !base.options.some((o) => o.value === "")
            ) {
              return {
                ...base,
                options: [{ value: "", label: "(empty)" }, ...base.options],
              };
            }
            return base;
          }),
        },
      ],
    };
  }, [props.fields, hardcodeFields, workspace, values]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) onClose();
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Record<string, any>) => {
      onSubmit(values);
      onClose();
    },
    [onSubmit, onClose],
  );

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content size="small" className="min-h-fit">
        <Dialog.Title>
          Add Context
          <Dialog.Close />
        </Dialog.Title>
        <Flex
          p="4"
          direction="column"
          gap="3"
          maxHeight="80vh"
          overflow="auto"
          onClick={(e) => e.stopPropagation()}
        >
          <DynamicForm
            schema={formSchema}
            onSubmit={handleSubmit}
            initialValues={values}
          />
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
